import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserAvatar } from "@/components/UserAvatar";
import {
  MessageSquare,
  User,
  Bot,
  Clock,
  Gamepad2,
  ChevronRight,
  Search
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { markMessagesAsRead } from "@/lib/messageUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getDeveloperProfileUrl, getDeveloperDisplayName } from "@/lib/developerUtils";
import { CreatorProfileModal } from "@/components/CreatorProfileModal";
import { hasDeveloperReportedCreator, getCreatorReportDetails, getCreatorTrustScore } from "@/lib/reportUtils";
import { useRef } from "react";

interface Message {
  id: string;
  message: string;
  sender_id: string;
  receiver_id: string;
  created_at: string;
  is_read: boolean;
  game: {
    id: string;
    title: string;
    cover_image_url: string | null;
  };
  sender: {
    display_name: string;
    avatar_url: string | null;
    user_type: string;
    studio_slug?: string | null;
    studio_name?: string | null;
  };
  receiver: {
    display_name: string;
    avatar_url: string | null;
    user_type: string;
    studio_name?: string | null;
  };
}

interface Conversation {
  game_id: string;
  game_title: string;
  game_cover: string | null;
  other_user: {
    id: string;
    display_name: string;
    avatar_url: string | null;
    user_type: string;
    studio_slug?: string | null;
    studio_name?: string | null;
  };
  last_message: {
    id: string;
    message: string;
    created_at: string;
    sender_id: string;
    is_read: boolean;
  };
  unread_count: number;
}

export default function Messages() {
  const { user } = useAuth();
  const { refetch: refetchUnreadCount } = useUnreadMessages();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [isViewingCreatorProfile, setIsViewingCreatorProfile] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<any>(null);
  const [loadingCreator, setLoadingCreator] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.id) {
      fetchUserProfile();
      fetchConversations();
    }
  }, [user?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_type, studio_slug")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        return;
      }

      setUserProfile(data);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };


  const fetchConversations = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from("messages")
        .select(`
          game_id,
          game:games(id, title, cover_image_url),
          sender_id,
          receiver_id,
          sender:profiles!messages_sender_id_fkey(id, display_name, avatar_url, user_type, studio_slug, studio_name, active_border_id, shop_items(metadata)),
          receiver:profiles!messages_receiver_id_fkey(id, display_name, avatar_url, user_type, studio_slug, studio_name, active_border_id, shop_items(metadata)),
          created_at,
          message,
          is_read
        `)
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching conversations:", error);
        toast.error("Failed to load conversations");
        return;
      }

      // Grouper les messages par jeu et utilisateur
      const conversationMap = new Map<string, Conversation>();

      data?.forEach((msg) => {
        const gameId = msg.game_id;
        const otherUserId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
        const otherUser = msg.sender_id === user.id ? msg.receiver : msg.sender;
        const key = `${gameId}-${otherUserId}`;

        if (!conversationMap.has(key)) {
          conversationMap.set(key, {
            game_id: gameId,
            game_title: msg.game.title,
            game_cover: msg.game.cover_image_url,
            other_user: otherUser,
            last_message: {
              id: msg.id,
              message: msg.message,
              created_at: msg.created_at,
              sender_id: msg.sender_id,
              is_read: msg.is_read
            },
            unread_count: 0
          });
        }

        const conversation = conversationMap.get(key)!;

        // Mettre à jour le dernier message si plus récent
        if (new Date(msg.created_at) > new Date(conversation.last_message.created_at)) {
          conversation.last_message = {
            id: msg.id,
            message: msg.message,
            created_at: msg.created_at,
            sender_id: msg.sender_id,
            is_read: msg.is_read
          };
        }

        // Compter les messages non lus
        if (msg.receiver_id === user.id && !msg.is_read) {
          conversation.unread_count++;
        }
      });

      setConversations(Array.from(conversationMap.values()));
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversation: Conversation) => {
    try {
      const { data, error } = await (supabase as any)
        .from("messages")
        .select(`
          *,
          game:games(id, title, cover_image_url),
          sender:profiles!messages_sender_id_fkey(
            id,
            display_name,
            avatar_url,
            user_type,
            studio_name,
            active_border_id,
            shop_items(metadata)
          ),
          receiver:profiles!messages_receiver_id_fkey(
            id,
            display_name,
            avatar_url,
            user_type,
            studio_name,
            active_border_id,
            shop_items(metadata)
          )
        `)
        .eq("game_id", conversation.game_id)
        .or(`and(sender_id.eq.${user?.id},receiver_id.eq.${conversation.other_user.id}),and(sender_id.eq.${conversation.other_user.id},receiver_id.eq.${user?.id})`)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching messages:", error);
        toast.error("Failed to load messages");
        return;
      }

      setMessages(data || []);

      // Marquer les messages comme lus immédiatement quand on ouvre la conversation
      const unreadMessages = data?.filter(msg => msg.receiver_id === user?.id && !msg.is_read) || [];
      if (unreadMessages.length > 0) {
        const messageIds = unreadMessages.map(msg => msg.id);
        const success = await markMessagesAsRead(messageIds, user.id, () => {
          // Mettre à jour le compteur de messages non lus immédiatement
          refetchUnreadCount();
        });
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    }
  };

  const fetchCreatorProfile = async (creatorId: string) => {
    setLoadingCreator(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select(`
          id,
          display_name,
          avatar_url,
          bio,
          channel_url,
          favorite_genres,
          twitch_url,
          youtube_url,
          tiktok_url,
          instagram_url,
          twitter_url,
          bluesky_url,
          steam_url,
          steam_id,
          is_verified,
          twitch_followers_count,
          twitch_subscribers_count,
          twitch_views_count,
          youtube_subscribers_count,
          youtube_views_count
        `)
        .eq("id", creatorId)
        .single();

      if (error) {
        console.error("Error fetching creator profile:", error);
        toast.error("Failed to load creator profile");
        return null;
      }

      // Ajouter les informations de report et trust score si l'utilisateur est un développeur
      if (user?.id && data) {
        const hasReported = await hasDeveloperReportedCreator(user.id, creatorId);
        const reportDetails = await getCreatorReportDetails(creatorId);
        const trustScore = await getCreatorTrustScore(creatorId);

        return {
          ...(data as any),
          steam_url: (data as any).steam_url || null,
          steam_id: (data as any).steam_id || null,
          has_reported: hasReported,
          report_details: reportDetails,
          trust_score: trustScore
        };
      }

      return data;
    } catch (error) {
      console.error("Error fetching creator profile:", error);
      toast.error("Failed to load creator profile");
      return null;
    } finally {
      setLoadingCreator(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return;

    try {
      setSending(true);
      const { error } = await supabase
        .from("messages" as any)
        .insert({
          game_id: selectedConversation.game_id,
          sender_id: user?.id,
          receiver_id: selectedConversation.other_user.id,
          message: newMessage.trim()
        });

      if (error) {
        console.error("Error sending message:", error);
        toast.error("Failed to send message");
        return;
      }

      setNewMessage("");
      fetchMessages(selectedConversation);
      fetchConversations(); // Refresh conversations
      refetchUnreadCount(); // Mettre à jour le compteur
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.game_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.other_user.display_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading messages...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 flex flex-col h-[calc(100vh-200px)]">
        <div className="flex items-center justify-between mb-8 flex-shrink-0">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Messages</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
          {/* Conversations List */}
          <div className="lg:col-span-1 flex flex-col min-h-0">
            <div className="mb-4 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 min-h-0 custom-scrollbar pr-2">
              {filteredConversations.length === 0 ? (
                <Card className="p-6 text-center">
                  <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
                  <p className="text-muted-foreground">
                    Start chatting with developers about your approved games!
                  </p>
                </Card>
              ) : (
                filteredConversations.map((conversation) => (
                  <Card
                    key={`${conversation.game_id}-${conversation.other_user.id}`}
                    className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${selectedConversation?.game_id === conversation.game_id &&
                        selectedConversation?.other_user.id === conversation.other_user.id
                        ? "bg-muted border-primary"
                        : ""
                      }`}
                    onClick={() => {
                      setSelectedConversation(conversation);
                      fetchMessages(conversation);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {conversation.game_cover ? (
                          <img
                            src={conversation.game_cover}
                            alt={conversation.game_title}
                            className="w-12 h-12 rounded-md object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center">
                            <Gamepad2 className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold truncate">{conversation.game_title}</h3>
                          <div className="flex items-center gap-2">
                            {conversation.unread_count > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {conversation.unread_count}
                              </Badge>
                            )}
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          with {conversation.other_user.user_type === 'developer'
                            ? getDeveloperDisplayName(conversation.other_user)
                            : conversation.other_user.display_name
                          }
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">
                            {formatTime(conversation.last_message.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Messages View */}
          <div className="lg:col-span-2 flex flex-col min-h-0">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <Card className="p-4 mb-4 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    {selectedConversation.game_cover ? (
                      <img
                        src={selectedConversation.game_cover}
                        alt={selectedConversation.game_title}
                        className="w-10 h-10 rounded-md object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => navigate(`/game/${selectedConversation.game_id}`)}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 bg-muted rounded-md flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => navigate(`/game/${selectedConversation.game_id}`)}
                      >
                        <Gamepad2 className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <h3
                        className="font-semibold cursor-pointer hover:text-primary transition-colors"
                        onClick={() => navigate(`/game/${selectedConversation.game_id}`)}
                      >
                        {selectedConversation.game_title}
                      </h3>
                      <p
                        className="text-sm text-muted-foreground cursor-pointer hover:text-primary transition-colors"
                        onClick={async () => {
                          if (selectedConversation.other_user.user_type === 'developer') {
                            // Si c'est l'utilisateur actuel, utiliser ses propres données
                            if (selectedConversation.other_user.id === user?.id) {
                              navigate(getDeveloperProfileUrl({
                                id: user.id,
                                studio_slug: userProfile?.studio_slug
                              }));
                            } else {
                              navigate(getDeveloperProfileUrl({
                                id: selectedConversation.other_user.id,
                                studio_slug: selectedConversation.other_user.studio_slug
                              }));
                            }
                          } else {
                            // Pour les créateurs, récupérer les données complètes et ouvrir le modal
                            const creatorData = await fetchCreatorProfile(selectedConversation.other_user.id);
                            if (creatorData) {
                              setSelectedCreator(creatorData);
                              setIsViewingCreatorProfile(true);
                            }
                          }
                        }}
                      >
                        with {selectedConversation.other_user.user_type === 'developer'
                          ? getDeveloperDisplayName(selectedConversation.other_user)
                          : selectedConversation.other_user.display_name
                        }
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Messages */}
                <Card className="flex-1 flex flex-col p-4 min-h-0">
                  <div className="flex-1 overflow-y-auto space-y-6 mb-4 min-h-0 messages-scrollbar pr-3">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${message.sender_id === user?.id ? "flex-row-reverse" : "flex-row"
                          }`}
                      >
                        <UserAvatar
                          src={message.sender.avatar_url || ""}
                          fallback={message.sender.display_name || "U"}
                          size="sm"
                          borderStyle={(message.sender as any).shop_items?.metadata?.style}
                          onClick={async () => {
                            if (message.sender.user_type === 'developer') {
                              // Si c'est l'utilisateur actuel, utiliser ses propres données
                              if (message.sender_id === user?.id) {
                                navigate(getDeveloperProfileUrl({
                                  id: user.id,
                                  studio_slug: userProfile?.studio_slug
                                }));
                              } else {
                                navigate(getDeveloperProfileUrl({
                                  id: message.sender_id,
                                  studio_slug: (message.sender as any).studio_slug
                                }));
                              }
                            } else {
                              // Pour les créateurs, récupérer les données complètes et ouvrir le modal
                              const creatorData = await fetchCreatorProfile(message.sender_id);
                              if (creatorData) {
                                setSelectedCreator(creatorData);
                                setIsViewingCreatorProfile(true);
                              }
                            }
                          }}
                        />
                        <div
                          className={`max-w-[70%] ${message.sender_id === user?.id
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                            } rounded-lg px-4 py-3`}
                        >
                          <div className="text-sm font-medium mb-1">
                            {message.sender.user_type === 'developer'
                              ? getDeveloperDisplayName(message.sender)
                              : message.sender.display_name
                            }
                          </div>
                          <div className="text-sm whitespace-pre-wrap">
                            {message.message}
                          </div>
                          <div className="text-xs opacity-70 mt-1">
                            {formatTime(message.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Message Input */}
                  <div className="flex gap-2 flex-shrink-0">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      disabled={sending}
                      className="flex-1"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                      size="sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              </>
            ) : (
              <Card className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Select a conversation</h3>
                  <p className="text-muted-foreground">
                    Choose a conversation from the list to start chatting
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Creator Profile Modal */}
      {selectedCreator && (
        <CreatorProfileModal
          creator={selectedCreator}
          isOpen={isViewingCreatorProfile}
          userType={userProfile?.user_type}
          onClose={() => {
            setIsViewingCreatorProfile(false);
            setSelectedCreator(null);
          }}
        />
      )}
    </Layout>
  );
}
